import { getAnnotationCategory } from './annotationCategories';
import {
  DEFAULT_FPS,
  formatClock,
  formatTimecode,
  framesToTimecode,
  isDropFrameRate,
  normalizeFps,
  secondsToFrames,
} from './timecode';

/**
 * Client-side exports of time-coded feedback: JSON (the studio's original
 * export), a plain CSV for spreadsheets, a marker CSV laid out like Premiere
 * Pro's marker list, and a CMX3600 EDL whose events carry the notes as
 * locators (DaVinci Resolve: Timeline > Import > Timeline Markers from EDL).
 *
 * The column layout and EDL shape match the backend's handoff package, so a
 * file exported here and the one in the .zip read the same way.
 */

export interface ExportableAnnotation {
  id?: string;
  time: number;
  endTime?: number | null;
  text: string;
  category?: string | null;
  color?: string | null;
}

interface ExportContext {
  /** Frame rate the timecodes are written in. */
  fps?: number;
  /** Shown in the EDL title and the JSON header. */
  title?: string;
  videoId?: string;
  /** Name of the source clip, written into each EDL event. */
  sourceName?: string;
}

const isRange = (annotation: ExportableAnnotation) =>
  typeof annotation.endTime === 'number' &&
  Number.isFinite(annotation.endTime) &&
  annotation.endTime > annotation.time;

const sorted = (annotations: ExportableAnnotation[]) =>
  [...annotations]
    .filter((a) => a && Number.isFinite(a.time))
    .sort((a, b) => a.time - b.time);

/** One line, no control characters. */
const singleLine = (value: string) => String(value ?? '').replace(/[\r\n\t]+/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * RFC 4180 cell. Text that a spreadsheet would treat as a formula (=, +, -, @
 * at the start) is prefixed with an apostrophe, so a note cannot run code when
 * the CSV is opened in Excel or Sheets.
 */
export const csvCell = (value: unknown, { text = false }: { text?: boolean } = {}): string => {
  let cell = value === null || value === undefined ? '' : String(value);
  if (text && /^[=+\-@\t\r]/.test(cell)) cell = `'${cell}`;
  if (/[",\r\n]/.test(cell)) cell = `"${cell.replace(/"/g, '""')}"`;
  return cell;
};

const csvRow = (cells: string[]) => cells.join(',');

/** The studio's JSON export (the original fields, plus exact seconds and timecodes). */
export const annotationsToJson = (annotations: ExportableAnnotation[], context: ExportContext = {}): string => {
  const fps = normalizeFps(context.fps);
  const list = sorted(annotations);
  return JSON.stringify(
    {
      videoName: context.title ?? null,
      videoId: context.videoId ?? null,
      exportDate: new Date().toISOString(),
      frameRate: fps,
      totalAnnotations: list.length,
      annotations: list.map((annotation) => {
        const category = getAnnotationCategory(annotation.category, annotation.color);
        return {
          time: formatClock(annotation.time),
          endTime: isRange(annotation) ? formatClock(annotation.endTime as number) : null,
          startSeconds: annotation.time,
          endSeconds: isRange(annotation) ? annotation.endTime : null,
          startTimecode: formatTimecode(annotation.time, fps),
          endTimecode: isRange(annotation) ? formatTimecode(annotation.endTime as number, fps) : null,
          text: annotation.text,
          category: annotation.category ?? category.id,
          categoryLabel: category.label,
          color: annotation.color ?? category.color,
        };
      }),
    },
    null,
    2
  );
};

/** Spreadsheet-friendly CSV: one row per note, timecodes and exact seconds. */
export const annotationsToCsv = (annotations: ExportableAnnotation[], context: ExportContext = {}): string => {
  const fps = normalizeFps(context.fps);
  const header = csvRow(['#', 'Start TC', 'End TC', 'Start (s)', 'End (s)', 'Duration (s)', 'Category', 'Note'].map((h) => csvCell(h)));
  const rows = sorted(annotations).map((annotation, index) => {
    const range = isRange(annotation);
    const end = range ? (annotation.endTime as number) : annotation.time;
    return csvRow([
      csvCell(index + 1),
      csvCell(formatTimecode(annotation.time, fps)),
      csvCell(range ? formatTimecode(end, fps) : ''),
      csvCell(annotation.time.toFixed(3)),
      csvCell(range ? end.toFixed(3) : ''),
      csvCell(range ? (end - annotation.time).toFixed(3) : ''),
      csvCell(getAnnotationCategory(annotation.category, annotation.color).label, { text: true }),
      csvCell(annotation.text, { text: true }),
    ]);
  });
  return [header, ...rows].join('\r\n') + '\r\n';
};

/**
 * Marker CSV in the column layout of Premiere Pro's marker list
 * (Name, Description, In, Out, Duration, Marker Type). Point notes get an Out
 * equal to their In and a zero duration.
 */
export const annotationsToPremiereMarkersCsv = (
  annotations: ExportableAnnotation[],
  context: ExportContext = {}
): string => {
  const fps = normalizeFps(context.fps);
  const header = csvRow(['Marker Name', 'Description', 'In', 'Out', 'Duration', 'Marker Type'].map((h) => csvCell(h)));
  const rows = sorted(annotations).map((annotation, index) => {
    const category = getAnnotationCategory(annotation.category, annotation.color);
    const end = isRange(annotation) ? (annotation.endTime as number) : annotation.time;
    const durationFrames = Math.max(0, secondsToFrames(end, fps) - secondsToFrames(annotation.time, fps));
    return csvRow([
      csvCell(`${index + 1}. ${category.label}`, { text: true }),
      csvCell(singleLine(annotation.text), { text: true }),
      csvCell(formatTimecode(annotation.time, fps)),
      csvCell(formatTimecode(end, fps)),
      csvCell(framesToTimecode(durationFrames, fps)),
      csvCell('Comment'),
    ]);
  });
  return [header, ...rows].join('\r\n') + '\r\n';
};

/** EDL text field: upper-case ASCII-safe title, single-line comments. */
const edlText = (value: string, max = 160) => {
  const line = singleLine(value).replace(/\|/g, '/');
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
};

/**
 * CMX3600 EDL with one event per note. Each event carries the note three ways
 * so the common importers all find it: a `* LOC:` locator (Avid-style), a
 * `* COMMENT:` line, and Resolve's `|C: |M: |D:` marker line.
 *
 * `recordStartSeconds` offsets record timecode for sequences that start at
 * 01:00:00:00 (Resolve's default) rather than 00:00:00:00.
 */
export const annotationsToEdl = (
  annotations: ExportableAnnotation[],
  context: ExportContext & { recordStartSeconds?: number } = {}
): string => {
  const fps = normalizeFps(context.fps);
  const dropFrame = isDropFrameRate(fps);
  const offsetFrames = secondsToFrames(Math.max(0, context.recordStartSeconds ?? 0), fps);
  const title = edlText(context.title || 'PithPlay feedback', 70).toUpperCase();
  const clip = edlText(context.sourceName || 'SOURCE', 60);
  const lines = [`TITLE: ${title}`, `FCM: ${dropFrame ? 'DROP FRAME' : 'NON-DROP FRAME'}`, ''];

  sorted(annotations).forEach((annotation, index) => {
    const category = getAnnotationCategory(annotation.category, annotation.color);
    const startFrame = secondsToFrames(annotation.time, fps);
    const endFrame = isRange(annotation)
      ? Math.max(startFrame + 1, secondsToFrames(annotation.endTime as number, fps))
      : startFrame + 1;
    const srcIn = framesToTimecode(startFrame, fps);
    const srcOut = framesToTimecode(endFrame, fps);
    const recIn = framesToTimecode(startFrame + offsetFrames, fps);
    const recOut = framesToTimecode(endFrame + offsetFrames, fps);
    const note = edlText(`${category.label}: ${annotation.text}`);
    const event = String(index + 1).padStart(3, '0');

    lines.push(`${event}  AX       V     C        ${srcIn} ${srcOut} ${recIn} ${recOut}`);
    lines.push(`* FROM CLIP NAME: ${clip}`);
    lines.push(`* LOC: ${recIn} ${category.locatorColor.padEnd(7)} ${note}`);
    lines.push(`* COMMENT: ${note}`);
    lines.push(` |C:${category.resolveColor} |M:${note} |D:${endFrame - startFrame}`);
    lines.push('');
  });

  return lines.join('\r\n');
};

/** A file name that is safe on every OS. */
export const safeFileName = (value: string, fallback = 'export') => {
  const base = String(value ?? '')
    .replace(/\.(mp4|mov|m4v|webm|mkv|avi|mxf)$/i, '')
    .replace(/[^\w\- ]+/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 80);
  return base || fallback;
};

export type AnnotationExportFormat = 'json' | 'csv' | 'premiere' | 'edl' | 'edl-hour';

export const ANNOTATION_EXPORT_FORMATS: Array<{
  format: AnnotationExportFormat;
  label: string;
  description: string;
}> = [
  { format: 'premiere', label: 'Premiere Pro markers (.csv)', description: 'Name, In, Out and Duration columns, one marker per note' },
  { format: 'edl', label: 'EDL with locators (.edl)', description: 'For sequences that start at 00:00:00:00' },
  { format: 'edl-hour', label: 'EDL with locators, 1-hour start (.edl)', description: 'For Resolve timelines that start at 01:00:00:00' },
  { format: 'csv', label: 'Spreadsheet (.csv)', description: 'Timecodes, exact seconds, category and note' },
  { format: 'json', label: 'JSON (.json)', description: 'The full annotation data' },
];

/** File name, MIME type and content for an export. */
export const buildAnnotationExport = (
  format: AnnotationExportFormat,
  annotations: ExportableAnnotation[],
  context: ExportContext = {}
): { filename: string; mimeType: string; content: string } => {
  const base = safeFileName(context.title || 'annotations');
  const fps = context.fps ?? DEFAULT_FPS;
  switch (format) {
    case 'csv':
      return { filename: `${base}_notes.csv`, mimeType: 'text/csv;charset=utf-8', content: annotationsToCsv(annotations, { ...context, fps }) };
    case 'premiere':
      return { filename: `${base}_premiere_markers.csv`, mimeType: 'text/csv;charset=utf-8', content: annotationsToPremiereMarkersCsv(annotations, { ...context, fps }) };
    case 'edl':
      return { filename: `${base}_markers.edl`, mimeType: 'text/plain;charset=utf-8', content: annotationsToEdl(annotations, { ...context, fps, recordStartSeconds: 0 }) };
    case 'edl-hour':
      return { filename: `${base}_markers_01h.edl`, mimeType: 'text/plain;charset=utf-8', content: annotationsToEdl(annotations, { ...context, fps, recordStartSeconds: 3600 }) };
    case 'json':
    default:
      return { filename: `${base}_annotations.json`, mimeType: 'application/json;charset=utf-8', content: annotationsToJson(annotations, { ...context, fps }) };
  }
};
